import React, { useState, useMemo, useEffect } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { 
  Calendar, 
  Clock, 
  DollarSign, 
  ExternalLink, 
  AlertTriangle, 
  CheckCircle, 
  FileText, 
  TrendingUp,
  Filter,
  ChevronRight,
  ChevronDown,
  Info,
  Bell,
  Download,
  RefreshCw
} from 'lucide-react';
import { ThemeColors, Spacing, Typography, BorderRadius, Shadows, Transitions } from '../../styles/ThemeStyles';
import { PayrollService } from '../../services/PayrollService';
import { PayrollPeriod } from '../../@types/payroll';

const PageContainer = styled.div<{ isMenuCollapsed: boolean }>`
  display: flex;
  min-height: 100vh;
  background-color: ${ThemeColors.background.default};
  margin-left: ${props => props.isMenuCollapsed ? '80px' : '280px'};
  transition: margin-left 0.3s ease;
`;

const MainContent = styled.div`
  flex: 1;
  padding: ${Spacing.xl};
  margin-top: 80px;
  max-width: 1400px;
  margin-left: auto;
  margin-right: auto;
`;

const PageHeader = styled.div`
  margin-bottom: ${Spacing.xl};
`;

const PageTitle = styled.h1`
  font-size: ${Typography.fontSize.xxxl};
  font-weight: ${Typography.fontWeight.bold};
  color: ${ThemeColors.text.primary};
  margin-bottom: ${Spacing.sm};
`;

const PageSubtitle = styled.p`
  font-size: ${Typography.fontSize.lg};
  color: ${ThemeColors.text.secondary};
  margin-bottom: 0;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: ${Spacing.lg};
  margin-bottom: ${Spacing.xl};
`;

const StatCard = styled.div`
  background: white;
  border-radius: ${BorderRadius.lg};
  padding: ${Spacing.lg};
  box-shadow: ${Shadows.sm};
  border: 1px solid ${ThemeColors.border.light};
  transition: ${Transitions.smooth};
  
  &:hover {
    box-shadow: ${Shadows.md};
    transform: translateY(-2px);
  }
`;

const StatHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: ${Spacing.md};
`;

const StatIcon = styled.div<{ color: string }>`
  width: 48px;
  height: 48px;
  border-radius: ${BorderRadius.lg};
  background: ${props => props.color};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: ${Spacing.md};
  
  svg {
    color: white;
    width: 24px;
    height: 24px;
  }
`;

const StatContent = styled.div`
  flex: 1;
`;

const StatLabel = styled.div`
  font-size: ${Typography.fontSize.sm};
  color: ${ThemeColors.text.secondary};
  margin-bottom: ${Spacing.xs};
`;

const StatValue = styled.div`
  font-size: ${Typography.fontSize.xl};
  font-weight: ${Typography.fontWeight.semibold};
  color: ${ThemeColors.text.primary};
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 400px;
  gap: ${Spacing.xl};
  
  @media (max-width: 1200px) {
    grid-template-columns: 1fr;
  }
`;

const MainPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacing.xl};
`;

const PanelCard = styled.div`
  background: white;
  border-radius: ${BorderRadius.lg};
  padding: ${Spacing.lg};
  box-shadow: ${Shadows.sm};
  border: 1px solid ${ThemeColors.border.light};
`;

const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${Spacing.lg};
  padding-bottom: ${Spacing.md};
  border-bottom: 1px solid ${ThemeColors.border.light};
`;

const PanelTitle = styled.h2`
  font-size: ${Typography.fontSize.xl};
  font-weight: ${Typography.fontWeight.semibold};
  color: ${ThemeColors.text.primary};
  margin: 0;
  display: flex;
  align-items: center;
  gap: ${Spacing.sm};
`;

const FilterContainer = styled.div`
  display: flex;
  gap: ${Spacing.sm};
  align-items: center;
`;

const FilterSelect = styled.select`
  padding: ${Spacing.sm} ${Spacing.md};
  border: 1px solid ${ThemeColors.border.light};
  border-radius: ${BorderRadius.md};
  font-size: ${Typography.fontSize.sm};
  background: white;
  color: ${ThemeColors.text.primary};
  cursor: pointer;
  
  &:focus {
    outline: none;
    border-color: ${ThemeColors.primary.main};
  }
`;

const ScheduleTable = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHeader = styled.thead`
  background: ${ThemeColors.background.light};
`;

const TableRow = styled.tr<{ status?: string }>`
  border-bottom: 1px solid ${ThemeColors.border.light};
  transition: ${Transitions.smooth};
  
  &:hover {
    background: ${ThemeColors.background.light};
  }
  
  ${props => props.status === 'current' && `
    background: linear-gradient(90deg, ${ThemeColors.primary.light}20, transparent);
    border-left: 4px solid ${ThemeColors.primary.main};
  `}
  
  ${props => props.status === 'overdue' && `
    background: linear-gradient(90deg, ${ThemeColors.error.light}20, transparent);
    border-left: 4px solid ${ThemeColors.error.main};
  `}
`;

const TableHeaderCell = styled.th`
  padding: ${Spacing.md};
  text-align: left;
  font-size: ${Typography.fontSize.sm};
  font-weight: ${Typography.fontWeight.semibold};
  color: ${ThemeColors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const TableCell = styled.td`
  padding: ${Spacing.md};
  font-size: ${Typography.fontSize.sm};
  color: ${ThemeColors.text.primary};
`;

const StatusBadge = styled.span<{ status: string }>`
  display: inline-flex;
  align-items: center;
  gap: ${Spacing.xs};
  padding: ${Spacing.xs} ${Spacing.sm};
  border-radius: ${BorderRadius.md};
  font-size: ${Typography.fontSize.xs};
  font-weight: ${Typography.fontWeight.medium};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${ThemeColors.text.primary};
  
  ${props => {
    switch (props.status) {
      case 'current':
        return `
          background: ${ThemeColors.primary.light};
        `;
      case 'upcoming':
        return `
          background: ${ThemeColors.warning.light};
        `;
      case 'completed':
        return `
          background: ${ThemeColors.success.light};
        `;
      case 'overdue':
        return `
          background: ${ThemeColors.error.light};
        `;
      default:
        return `
          background: ${ThemeColors.gray[100]};
        `;
    }
  }}
`;

const QuickActionsPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacing.lg};
`;

const ActionCard = styled.a`
  display: block;
  background: white;
  border-radius: ${BorderRadius.lg};
  padding: ${Spacing.lg};
  box-shadow: ${Shadows.sm};
  border: 1px solid ${ThemeColors.border.light};
  text-decoration: none;
  color: inherit;
  transition: ${Transitions.smooth};
  
  &:hover {
    box-shadow: ${Shadows.md};
    transform: translateY(-2px);
    text-decoration: none;
    color: inherit;
  }
`;

const ActionHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: ${Spacing.md};
`;

const ActionIcon = styled.div<{ color: string }>`
  width: 48px;
  height: 48px;
  border-radius: ${BorderRadius.lg};
  background: ${props => props.color};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: ${Spacing.md};
  
  svg {
    color: white;
    width: 24px;
    height: 24px;
  }
`;

const ActionContent = styled.div`
  flex: 1;
`;

const ActionTitle = styled.h3`
  font-size: ${Typography.fontSize.lg};
  font-weight: ${Typography.fontWeight.semibold};
  color: ${ThemeColors.text.primary};
  margin: 0 0 ${Spacing.xs} 0;
`;

const ActionDescription = styled.p`
  font-size: ${Typography.fontSize.sm};
  color: ${ThemeColors.text.secondary};
  margin: 0;
`;

const ExternalLinkIcon = styled(ExternalLink)`
  width: 16px;
  height: 16px;
  color: ${ThemeColors.text.secondary};
  margin-left: ${Spacing.xs};
`;

const NotesSection = styled.div`
  background: ${ThemeColors.background.light};
  border-radius: ${BorderRadius.lg};
  padding: ${Spacing.lg};
  margin-top: ${Spacing.lg};
`;

const NotesTitle = styled.h3`
  font-size: ${Typography.fontSize.lg};
  font-weight: ${Typography.fontWeight.semibold};
  color: ${ThemeColors.text.primary};
  margin: 0 0 ${Spacing.md} 0;
  display: flex;
  align-items: center;
  gap: ${Spacing.sm};
`;

const NotesList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const NotesItem = styled.li`
  font-size: ${Typography.fontSize.sm};
  color: ${ThemeColors.text.secondary};
  margin-bottom: ${Spacing.sm};
  padding-left: ${Spacing.md};
  position: relative;
  
  &::before {
    content: '•';
    color: ${ThemeColors.primary.main};
    font-weight: bold;
    position: absolute;
    left: 0;
  }
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: ${Spacing.xl};
`;

const Spinner = styled.div`
  width: 40px;
  height: 40px;
  border: 4px solid ${ThemeColors.border.light};
  border-top: 4px solid ${ThemeColors.primary.main};
  border-radius: 50%;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const LoadingText = styled.p`
  margin-left: ${Spacing.md};
  color: ${ThemeColors.text.secondary};
`;

export const PayrollScreenDesktop = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [payrollPeriods, setPayrollPeriods] = useState<PayrollPeriod[]>([]);
  const [error, setError] = useState<string | null>(null);

  /**
   * Load payroll periods from Firebase
   */
  useEffect(() => {
    const loadPayrollPeriods = async () => {
      try {
        setLoading(true);
        setError(null);
        const periods = await PayrollService.getPayrollPeriods();
        setPayrollPeriods(periods);
        console.log('[PayrollScreenDesktop] Loaded', periods.length, 'payroll periods');
      } catch (err: any) {
        console.error('[PayrollScreenDesktop] Error loading payroll periods:', err);
        setError(err.message || 'Failed to load payroll periods');
      } finally {
        setLoading(false);
      }
    };

    loadPayrollPeriods();
  }, []);

  const filteredSchedule = useMemo(() => {
    if (filter === 'all') return payrollPeriods;
    return payrollPeriods.filter(period => period.status === filter);
  }, [payrollPeriods, filter]);

  const currentPeriod = useMemo(() => {
    return payrollPeriods.find(period => period.status === 'current');
  }, [payrollPeriods]);

  /** Period used for stats cards: prefer current, else overdue, else next upcoming. Avoids N/A when no "current" period. */
  const displayPeriod = useMemo(() => {
    if (currentPeriod) return currentPeriod;
    const overdue = payrollPeriods.find(p => p.status === 'overdue');
    if (overdue) return overdue;
    const upcoming = payrollPeriods.find(p => p.status === 'upcoming');
    return upcoming ?? null;
  }, [payrollPeriods, currentPeriod]);

  const nextPayDate = useMemo(() => {
    return displayPeriod?.payDate ?? null;
  }, [displayPeriod]);

  const upcomingDeadlines = useMemo(() => {
    return payrollPeriods.filter(period => 
      period.status === 'current' || period.status === 'upcoming'
    ).slice(0, 3);
  }, [payrollPeriods]);

  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'current':
        return <Clock size={16} />;
      case 'upcoming':
        return <Calendar size={16} />;
      case 'completed':
        return <CheckCircle size={16} />;
      case 'overdue':
        return <AlertTriangle size={16} />;
      default:
        return <Calendar size={16} />;
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'current':
        return t('payroll.currentPayPeriod');
      case 'upcoming':
        return t('payroll.upcoming');
      case 'completed':
        return t('payroll.completed');
      case 'overdue':
        return t('payroll.overdue');
      default:
        return status;
    }
  };

  const renderLayout = (children: React.ReactNode) => (
    <PageContainer isMenuCollapsed={false}>
      {children}
    </PageContainer>
  );

  if (loading) {
    return renderLayout(
      <MainContent>
        <LoadingSpinner>
          <Spinner />
          <LoadingText>{t('payroll.loading')}</LoadingText>
        </LoadingSpinner>
      </MainContent>
    );
  }

  if (error) {
    return renderLayout(
      <MainContent>
        <PageHeader>
          <PageTitle>{t('payroll.title')}</PageTitle>
          <PageSubtitle style={{ color: ThemeColors.error.main }}>{error}</PageSubtitle>
        </PageHeader>
      </MainContent>
    );
  }

  return renderLayout(
    <MainContent>
      <PageHeader>
        <PageTitle>{t('payroll.title')}</PageTitle>
        <PageSubtitle>{t('payroll.subtitle')}</PageSubtitle>
      </PageHeader>

          {/* Stats Cards */}
          <StatsGrid>
            <StatCard>
              <StatHeader>
                <StatIcon color={ThemeColors.primary.main}>
                  <Calendar />
                </StatIcon>
                <StatContent>
                  <StatLabel>{t('payroll.currentPayPeriod')}</StatLabel>
                  <StatValue>Period #{displayPeriod?.periodNumber ?? 'N/A'}</StatValue>
                </StatContent>
              </StatHeader>
            </StatCard>

            <StatCard>
              <StatHeader>
                <StatIcon color={ThemeColors.success.main}>
                  <DollarSign />
                </StatIcon>
                <StatContent>
                  <StatLabel>{t('payroll.nextPayDate')}</StatLabel>
                  <StatValue>{nextPayDate ? formatDate(nextPayDate) : 'N/A'}</StatValue>
                </StatContent>
              </StatHeader>
            </StatCard>

            <StatCard>
              <StatHeader>
                <StatIcon color={ThemeColors.warning.main}>
                  <Clock />
                </StatIcon>
                <StatContent>
                  <StatLabel>{t('payroll.submitDeadline')}</StatLabel>
                  <StatValue>
                    {displayPeriod ? formatDate(displayPeriod.submitDate) : 'N/A'}
                    {displayPeriod?.daysRemaining != null && (
                      <span style={{ fontSize: '14px', color: ThemeColors.text.secondary, marginLeft: '8px' }}>
                        {displayPeriod.daysRemaining >= 0
                          ? `(${displayPeriod.daysRemaining} ${t('payroll.daysRemaining')})`
                          : `(${t('payroll.pastDeadline', 'Past deadline')})`}
                      </span>
                    )}
                  </StatValue>
                </StatContent>
              </StatHeader>
            </StatCard>
          </StatsGrid>

          <ContentGrid>
            <MainPanel>
              {/* Payroll Schedule */}
              <PanelCard>
                <PanelHeader>
                  <PanelTitle>
                    <Calendar />
                    {t('payroll.payrollSchedule')}
                  </PanelTitle>
                  <FilterContainer>
                    <Filter size={16} />
                    <FilterSelect value={filter} onChange={(e) => setFilter(e.target.value)}>
                      <option value="all">{t('payroll.allPeriods')}</option>
                      <option value="current">{t('payroll.currentPayPeriod')}</option>
                      <option value="upcoming">{t('payroll.upcoming')}</option>
                      <option value="completed">{t('payroll.completed')}</option>
                    </FilterSelect>
                  </FilterContainer>
                </PanelHeader>

                <ScheduleTable>
                  <Table>
                    <TableHeader>
                      <tr>
                        <TableHeaderCell>{t('payroll.payPeriod')}</TableHeaderCell>
                        <TableHeaderCell>{t('payroll.startDate')}</TableHeaderCell>
                        <TableHeaderCell>{t('payroll.endDate')}</TableHeaderCell>
                        <TableHeaderCell>{t('payroll.submitDate')}</TableHeaderCell>
                        <TableHeaderCell>{t('payroll.payDate')}</TableHeaderCell>
                        <TableHeaderCell>{t('common.status')}</TableHeaderCell>
                      </tr>
                    </TableHeader>
                    <tbody>
                      {filteredSchedule.map((period) => {
                        const status = period.status ?? 'upcoming';
                        return (
                        <TableRow key={period.id} status={status}>
                          <TableCell>#{period.periodNumber}</TableCell>
                          <TableCell>{formatDate(period.startDate)}</TableCell>
                          <TableCell>{formatDate(period.endDate)}</TableCell>
                          <TableCell>{formatDate(period.submitDate)}</TableCell>
                          <TableCell>{formatDate(period.payDate)}</TableCell>
                          <TableCell>
                            <StatusBadge status={status}>
                              {getStatusIcon(status)}
                              {getStatusText(status)}
                            </StatusBadge>
                          </TableCell>
                        </TableRow>
                        );
                      })}
                    </tbody>
                  </Table>
                </ScheduleTable>
              </PanelCard>

              {/* Important Notes */}
              <NotesSection>
                <NotesTitle>
                  <Info />
                  {t('payroll.importantNotes')}
                </NotesTitle>
                <NotesList>
                  <NotesItem>{t('payroll.note1')}</NotesItem>
                  <NotesItem>{t('payroll.note2')}</NotesItem>
                  <NotesItem>{t('payroll.note3')}</NotesItem>
                  <NotesItem>{t('payroll.note4')}</NotesItem>
                  <NotesItem>{t('payroll.note5')}</NotesItem>
                </NotesList>
              </NotesSection>
            </MainPanel>

            <QuickActionsPanel>
              {/* Quick Actions */}
              <PanelCard>
                <PanelTitle>
                  <Bell />
                  {t('payroll.quickActions')}
                </PanelTitle>

                <ActionCard href="https://www.concursolutions.com/" target="_blank" rel="noopener noreferrer">
                  <ActionHeader>
                    <ActionIcon color="#0070f3">
                      <img src="/assets/sap-concur-logo.svg" alt="SAP Concur" style={{ width: '24px', height: 'auto' }} />
                    </ActionIcon>
                    <ActionContent>
                      <ActionTitle>
                        {t('payroll.expenseReports')}
                        <ExternalLinkIcon />
                      </ActionTitle>
                      <ActionDescription>{t('payroll.accessExpenseReports')}</ActionDescription>
                    </ActionContent>
                  </ActionHeader>
                </ActionCard>

                <ActionCard href="https://online.adp.com/signin/v1/?APPID=WFNPortal&productId=80e309c3-7085-bae1-e053-3505430b5495&returnURL=https://workforcenow.adp.com/&callingAppId=WFN" target="_blank" rel="noopener noreferrer">
                  <ActionHeader>
                    <ActionIcon color="#ff6b35">
                      <img src="/assets/adp-logo.svg" alt="ADP" style={{ width: '24px', height: 'auto' }} />
                    </ActionIcon>
                    <ActionContent>
                      <ActionTitle>
                        {t('payroll.salaryAccess')}
                        <ExternalLinkIcon />
                      </ActionTitle>
                      <ActionDescription>{t('payroll.accessSalaryInfo')}</ActionDescription>
                    </ActionContent>
                  </ActionHeader>
                </ActionCard>
              </PanelCard>

              {/* Upcoming Deadlines */}
              <PanelCard>
                <PanelTitle>
                  <Clock />
                  {t('payroll.upcomingDeadlines')}
                </PanelTitle>

                {upcomingDeadlines.map((deadline) => {
                  const status = deadline.status ?? 'upcoming';
                  return (
                  <div key={deadline.id} style={{ 
                    padding: `${Spacing.md} 0`, 
                    borderBottom: '1px solid #f0f0f0',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <div>
                      <div style={{ fontWeight: '600', color: ThemeColors.text.primary }}>
                        Period #{deadline.periodNumber}
                      </div>
                      <div style={{ fontSize: '14px', color: ThemeColors.text.secondary }}>
                        {formatDate(deadline.submitDate)}
                      </div>
                    </div>
                    <StatusBadge status={status}>
                      {getStatusIcon(status)}
                      {getStatusText(status)}
                    </StatusBadge>
                  </div>
                  );
                })}
              </PanelCard>
            </QuickActionsPanel>
          </ContentGrid>
        </MainContent>
  );
};

